import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Network as NetworkIcon, RefreshCw, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Network } from '../../types';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { networksApi, NetworkFormData as APINetworkFormData } from '../../api/networks';

interface NetworkManagementProps {
  networks: Network[];
  onRefresh: () => void;
  onManageDomains?: (network: Network) => void;
  isLoading?: boolean;
  // Backward compatibility: if parent still passes old handlers we ignore them gracefully
  onNetworkAdd?: (network: { name: string; bqKey: string }) => Promise<void>;
  onNetworkEdit?: (oldName: string, network: { name: string; bqKey: string }) => Promise<void>;
  onNetworkDelete?: (networkName: string) => Promise<void>;
}

interface NetworkFormData {
  name: string;
  bqKey: string;
  status: string;
  country: string;
  currency: string;
}

export const NetworkManagement: React.FC<NetworkManagementProps> = ({
  networks,
  onNetworkAdd,
  onNetworkEdit,
  onNetworkDelete,
  onRefresh,
  onManageDomains,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNetwork, setEditingNetwork] = useState<string | null>(null);
  const [formData, setFormData] = useState<NetworkFormData>({
    name: '',
    bqKey: '',
    status: 'PROSPECT',
    country: '',
    currency: ''
  });
  const [formErrors, setFormErrors] = useState<Partial<NetworkFormData>>({});

  const resetForm = () => {
    setFormData({ name: '', bqKey: '', status: 'PROSPECT', country: '', currency: '' });
    setFormErrors({});
    setEditingNetwork(null);
  };

  const validateForm = (): boolean => {
    const errors: Partial<NetworkFormData> = {};
    
    if (!formData.name.trim()) {
      errors.name = t('networkManagement.errors.nameRequired');
    }
    
    if (!formData.bqKey.trim()) {
      errors.bqKey = t('networkManagement.errors.bqKeyRequired');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const payload: APINetworkFormData = {
        name: formData.name,
        bqKey: formData.bqKey,
        status: formData.status,
        country: formData.country || undefined,
        currency: formData.currency || undefined
      };
      if (editingNetwork) {
        if (onNetworkEdit) await onNetworkEdit(editingNetwork, payload);
        else await networksApi.update(networks.find(n=>n.name===editingNetwork)?.id || 0, payload);
      } else {
        if (onNetworkAdd) await onNetworkAdd(payload);
        else await networksApi.create(payload);
      }
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving network:', error);
    }
  };

  const handleEdit = (network: Network) => {
    setEditingNetwork(network.name);
    setFormData({
      name: network.name,
      bqKey: network.bqKey,
      status: (network as any).status || 'PROSPECT',
      country: (network as any).country || '',
      currency: (network as any).currency || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (networkName: string) => {
    if (!window.confirm(t('networkManagement.confirmDelete', { name: networkName }))) return;
    try {
      if (onNetworkDelete) await onNetworkDelete(networkName);
      else {
        const target = networks.find(n => n.name === networkName);
        if (target) await networksApi.delete(target.id);
      }
    } catch (error) {
      console.error('Error deleting network:', error);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  return (
    <Card className="border-none shadow-sm dark:shadow-lg h-full flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg flex items-center gap-2">
            <NetworkIcon className="h-5 w-5 text-primary" />
            {t('networkManagement.title')}
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={isLoading}
              className="h-8 w-8 p-0"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  onClick={() => {
                    resetForm();
                    setIsDialogOpen(true);
                  }}
                  className="h-8"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t('networkManagement.add')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>
                    {editingNetwork 
                      ? t('networkManagement.editNetwork') 
                      : t('networkManagement.addNetwork')
                    }
                  </DialogTitle>
                  <DialogDescription>
                    {t('networkManagement.formDescription')}
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">{t('networkManagement.name')}</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t('networkManagement.namePlaceholder')}
                    />
                    {formErrors.name && (
                      <span className="text-sm text-destructive">{formErrors.name}</span>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="bqKey">{t('networkManagement.bqKey')}</Label>
                    <Input
                      id="bqKey"
                      value={formData.bqKey}
                      onChange={(e) => setFormData({ ...formData, bqKey: e.target.value })}
                      placeholder={t('networkManagement.bqKeyPlaceholder')}
                    />
                    {formErrors.bqKey && (
                      <span className="text-sm text-destructive">{formErrors.bqKey}</span>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="status">{t('networkManagement.status') || 'Status'}</Label>
                    <Select value={formData.status} onValueChange={(v)=>setFormData({...formData,status:v})}>
                      <SelectTrigger id="status">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        {['PROSPECT','ONBOARDING','ACTIVE','PAUSED','TERMINATED','ARCHIVED'].map(s=> (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2 grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="country">{t('networkManagement.country') || 'Country'}</Label>
                      <Input id="country" value={formData.country} onChange={e=>setFormData({...formData,country:e.target.value})} placeholder="France" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="currency">{t('networkManagement.currency') || 'Currency'}</Label>
                      <Input id="currency" value={formData.currency} onChange={e=>setFormData({...formData,currency:e.target.value.toUpperCase()})} placeholder="EUR" maxLength={3} />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={handleDialogClose}>
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleSubmit}>
                    {t('common.save')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 flex-1 flex flex-col min-h-0">
        {networks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <NetworkIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>{t('networkManagement.noNetworks')}</p>
          </div>
        ) : (
          <ScrollArea className="flex-1 min-h-0">
            <div className="space-y-2 pr-4 pb-4">
              {networks.map((network) => (
                <div
                  key={network.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border/40 bg-muted/20 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Badge variant="outline" className="bg-background">
                      {network.name}
                    </Badge>
                    <span className="text-sm text-muted-foreground">{network.bqKey}</span>
                    {network.domains && (
                      <span className="text-xs text-muted-foreground bg-background px-2 py-1 rounded-full">
                        {network.domains.length} domaine{network.domains.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {onManageDomains && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onManageDomains(network)}
                        className="h-8 w-8 p-0 text-primary hover:text-primary/80 hover:bg-primary/10"
                        title={t('networkManagement.manageDomains')}
                      >
                        <Globe className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(network)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(network.name)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};